import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
} from 'remotion';

/**
 * #313 自动高度展开动画 (Auto Height Expand)
 *
 * 技术：模拟 height: auto 动画
 * - 从固定高度到内容高度
 * - 多个卡片依次展开
 * - 内容渐显效果
 * - Spring 物理动画
 */

export type AutoHeightExpandCard = {
  title: string;
  content: string;
  height: number;
  delay: number;
};

export type AutoHeightExpandProps = {
  /** 主标题 */
  title?: string;
  /** 底部说明文字 */
  subtitle?: string;
  /** 技术参数说明（留空自动生成） */
  techInfo?: string;
  /** 卡片列表 */
  cards?: AutoHeightExpandCard[];
  /** 背景色 */
  backgroundColor?: string;
  /** 卡片背景色 */
  cardBackgroundColor?: string;
  /** 卡片边框色 */
  cardBorderColor?: string;
  /** 主文字颜色 */
  primaryColor?: string;
  /** 次要文字颜色 */
  secondaryColor?: string;
  /** 强调色（箭头颜色） */
  accentColor?: string;
  /** 折叠时卡片高度 */
  collapsedHeight?: number;
  /** 卡片组宽度 */
  cardGroupWidth?: number;
  /** Spring 物理动画参数 */
  springConfig?: {
    damping?: number;
    stiffness?: number;
    mass?: number;
  };
};

const DEFAULT_CARDS: AutoHeightExpandCard[] = [
  {
    title: '视频编辑',
    content: '支持视频剪辑、拼接、转场等多种编辑功能，提供专业级的时间轴控制',
    height: 200,
    delay: 0,
  },
  {
    title: '音频处理',
    content: '音频降噪、音量调节、音频可视化等功能，支持多种音频格式',
    height: 180,
    delay: 20,
  },
  {
    title: '特效渲染',
    content: 'GPU 加速渲染、粒子系统、3D 效果、滤镜等高级视觉特效',
    height: 220,
    delay: 40,
  },
];

export const AutoHeightExpand: React.FC<AutoHeightExpandProps> = ({
  title = '自动高度展开',
  subtitle = '模拟 height: auto 的展开效果',
  techInfo,
  cards = DEFAULT_CARDS,
  backgroundColor = '#0a0a0a',
  cardBackgroundColor = '#1a1a1a',
  cardBorderColor = '#333',
  primaryColor = '#fff',
  secondaryColor = '#aaa',
  accentColor = '#3b82f6',
  collapsedHeight = 60,
  cardGroupWidth = 900,
  springConfig,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const mergedSpringConfig = {
    damping: springConfig?.damping ?? 20,
    stiffness: springConfig?.stiffness ?? 150,
    mass: springConfig?.mass ?? 0.8,
  };

  const resolvedTechInfo =
    techInfo ??
    `Cards: ${cards.length} | Delay: ${cards[0]?.delay ?? 0}帧 | Spring 物理动画`;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 背景网格 */}
      <svg width="100%" height="100%" style={{ position: 'absolute' }}>
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1a1a1a" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          fontSize: 65,
          fontWeight: 'bold',
          color: primaryColor,
          textAlign: 'center',
        }}
      >
        {title}
      </div>

      {/* 卡片组 */}
      <div
        style={{
          position: 'absolute',
          top: 220,
          width: cardGroupWidth,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {cards.map((card, index) => {
          const cardProgress = spring({
            frame: frame - card.delay,
            fps,
            config: mergedSpringConfig,
          });

          const currentHeight = interpolate(
            cardProgress,
            [0, 1],
            [collapsedHeight, card.height],
          );

          const contentOpacity = interpolate(cardProgress, [0.3, 0.8], [0, 1]);

          const arrowRotation = interpolate(cardProgress, [0, 1], [0, 180]);

          return (
            <div
              key={index}
              style={{
                backgroundColor: cardBackgroundColor,
                borderRadius: 16,
                border: `2px solid ${cardBorderColor}`,
                overflow: 'hidden',
                height: currentHeight,
                transition: 'height 0.3s ease',
              }}
            >
              {/* 标题栏 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px 25px',
                  borderBottom:
                    cardProgress > 0.5 ? `1px solid ${cardBorderColor}` : 'none',
                }}
              >
                <div
                  style={{ fontSize: 30, fontWeight: 'bold', color: primaryColor }}
                >
                  {card.title}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    color: accentColor,
                    transform: `rotate(${arrowRotation}deg)`,
                  }}
                >
                  ▼
                </div>
              </div>

              {/* 内容区 */}
              <div
                style={{
                  padding: '20px 25px',
                  opacity: contentOpacity,
                }}
              >
                <div
                  style={{ fontSize: 24, color: secondaryColor, lineHeight: 1.6 }}
                >
                  {card.content}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 底部说明 */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          fontSize: 30,
          color: '#888',
          textAlign: 'center',
        }}
      >
        {subtitle}
      </div>

      {/* 技术参数 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          fontSize: 24,
          color: '#666',
          textAlign: 'center',
        }}
      >
        {resolvedTechInfo}
      </div>
    </AbsoluteFill>
  );
};
